// src/pages/budgeting/boq/FOSegmentBOQForm.jsx - COMPLETE & READY TO USE
import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, 
  Save, 
  Plus, 
  Trash2, 
  ChevronDown,
  ChevronRight,
  Package,
  Info
} from 'lucide-react';
import toast from 'react-hot-toast';
import ItemPickerModal from '../../../components/boq/ItemPickerModal';
import foSegmentBOQService from '../../../services/foSegmentBOQService';
import budgetCategoryService from '../../../services/budgetCategoryService';

const FOSegmentBOQForm = ({ segment, onBack, onSave }) => {
  // ============= STATE MANAGEMENT =============
  
  // Categories data
  const [categories, setCategories] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  
  // BOQ data per category: { categoryId: { items: [...], subtotal: 0 } }
  const [boqData, setBOQData] = useState({});
  
  // UI state
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Item Picker Modal
  const [showItemPicker, setShowItemPicker] = useState(false);
  const [currentCategoryId, setCurrentCategoryId] = useState(null);
  
  // Price coefficient (will be fetched or default to 1.0)
  const [priceCoefficient, setPriceCoefficient] = useState(1.0);

  // ============= LIFECYCLE =============
  
  useEffect(() => {
    console.log('🔍 SEGMENT DATA RECEIVED:', segment);
    console.log('🔍 priceCoefficient:', segment.priceCoefficient);
    console.log('🔍 price_coefficient:', segment.price_coefficient);
    // Initialize price coefficient from segment or default
    const coefficientValue = segment.priceCoefficient || 
                             segment.price_coefficient || 
                             1.0;
    console.log('✅ FINAL COEFFICIENT:', coefficientValue);                         
    setPriceCoefficient(coefficientValue);
    
    loadCategories();
    loadExistingBOQ();
  }, [segment.id]);

  // ============= DATA LOADING =============
  
  /**
   * Load FO Budget Categories (001-005)
   */
  const loadCategories = async () => {
    setIsLoadingCategories(true);
    try {
      console.log('📂 Loading budget categories...');
      const allCategories = await budgetCategoryService.getAllCategories();
      console.log('✅ Received categories:', allCategories);
      
      // Filter only FO categories (001-005)
      const foCategories = allCategories.filter(cat => 
        cat.projectType === 'FO'
      );
      
      console.log('✅ Filtered FO categories:', foCategories);
      
      if (foCategories.length === 0) {
        console.warn('⚠️ No FO categories found!');
        toast.error('No FO budget categories available');
        return;
      }
      
      setCategories(foCategories);
      
      // Expand all categories by default
      setExpandedCategories(foCategories.map(cat => cat.id));
      
      console.log('✅ Loaded FO categories:', foCategories);
    } catch (error) {
      console.error('❌ Error loading categories:', error);
      console.error('❌ Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      toast.error('Failed to load budget categories: ' + (error.message || 'Unknown error'));
    } finally {
      setIsLoadingCategories(false);
    }
  };
  
  /**
   * Load existing BOQ data if any
   */
  const loadExistingBOQ = async () => {
    setIsLoading(true);
    try {
      const boqs = await foSegmentBOQService.getBOQsBySegmentId(segment.id);
      
      if (boqs && boqs.length > 0) {
        // Transform to { categoryId: { items: [...], subtotal: ... } }
        const transformed = {};
        boqs.forEach(boq => {
          transformed[boq.budgetCategoryId] = {
            boqId: boq.id,
            items: boq.items || [],
            subtotal: boq.subtotalAmount || 0,
            remarks: boq.remarks || ''
          };
        });
        
        setBOQData(transformed);
        console.log('✅ Loaded existing BOQ:', transformed);
      }
    } catch (error) {
      console.error('❌ Error loading BOQ:', error);
      // Don't show error toast - BOQ might not exist yet
    } finally {
      setIsLoading(false);
    }
  };

  // ============= HELPER FUNCTIONS =============
  
  /**
   * Format currency to Indonesian Rupiah
   */
  const formatCurrency = (amount) => {
    if (!amount) return 'Rp 0';
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  /**
   * Format percentage with sign
   */
  const formatPercentage = (value) => {
    if (!value) return '0.00%';
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}%`;
  };
  
  /**
   * Calculate price adjustment
   */
  const calculatePriceAdjustment = (item) => {
    // Adjusted = Base × Coefficient
    const adjustedPrice = item.baseUnitPrice * priceCoefficient;
    
    // Final = Override OR Adjusted
    const finalPrice = item.isPriceOverridden ? item.overriddenUnitPrice : adjustedPrice;
    
    // Total = Final × Volume
    const totalPrice = finalPrice * item.volume;
    
    return {
      adjustedUnitPrice: adjustedPrice,
      finalUnitPrice: finalPrice,
      totalPrice: totalPrice
    };
  };
  
  /**
   * Calculate subtotal for a category
   */
  const calculateSubtotal = (categoryId) => {
    const items = boqData[categoryId]?.items || [];
    return items.reduce((sum, item) => {
      const { totalPrice } = calculatePriceAdjustment(item);
      return sum + totalPrice;
    }, 0);
  };
  
  /**
   * Calculate grand total across all categories
   */
  const calculateGrandTotal = () => {
    return categories.reduce((sum, category) => {
      return sum + calculateSubtotal(category.id);
    }, 0);
  };

  // ============= EVENT HANDLERS =============
  
  /**
   * Toggle category expand/collapse
   */
  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };
  
  /**
   * Open item picker for a category
   */
  const handleAddItem = (categoryId) => {
    setCurrentCategoryId(categoryId);
    setShowItemPicker(true);
  };
  
  /**
   * Handle item selection from picker
   */
  const handleItemSelect = (item) => {
    if (!currentCategoryId) return;
    
    // Create new BOQ item with price calculation
    const newItem = {
      tempId: `temp-${Date.now()}`,
      itemId: item.id,
      itemNumber: item.itemNumber,
      itemCode: item.itemCode,
      itemDescription: item.itemDescription,
      itemType: item.itemType,
      unit: item.unit,
      volume: 1, // Default volume
      baseUnitPrice: item.defaultPrice || 0,
      priceCoefficient: priceCoefficient,
      isPriceOverridden: false,
      overriddenUnitPrice: 0,
      remarks: '',
      sequenceOrder: (boqData[currentCategoryId]?.items?.length || 0) + 1
    };
    
    // Calculate prices
    const { adjustedUnitPrice, finalUnitPrice, totalPrice } = calculatePriceAdjustment(newItem);
    newItem.adjustedUnitPrice = adjustedUnitPrice;
    newItem.finalUnitPrice = finalUnitPrice;
    newItem.totalPrice = totalPrice;
    
    // Add to BOQ data
    setBOQData(prev => ({
      ...prev,
      [currentCategoryId]: {
        ...prev[currentCategoryId],
        items: [...(prev[currentCategoryId]?.items || []), newItem]
      }
    }));
    
    setShowItemPicker(false);
    toast.success('Item added to BOQ');
  };
  
  /**
   * Handle item field change (volume, override price, remarks)
   */
  const handleItemFieldChange = (categoryId, itemId, field, value) => {
    setBOQData(prev => {
      const categoryData = prev[categoryId] || { items: [] };
      const items = categoryData.items.map(item => {
        if ((item.tempId || item.id) === itemId) {
          const updatedItem = { ...item, [field]: value };
          
          // If volume or override changed, recalculate prices
          if (field === 'volume' || field === 'overriddenUnitPrice' || field === 'isPriceOverridden') {
            const { adjustedUnitPrice, finalUnitPrice, totalPrice } = calculatePriceAdjustment(updatedItem);
            updatedItem.adjustedUnitPrice = adjustedUnitPrice;
            updatedItem.finalUnitPrice = finalUnitPrice;
            updatedItem.totalPrice = totalPrice;
          }
          
          return updatedItem;
        }
        return item;
      });
      
      return {
        ...prev,
        [categoryId]: {
          ...categoryData,
          items
        }
      };
    });
  };
  
  /**
   * Delete item from BOQ
   */
  const handleDeleteItem = (categoryId, itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    setBOQData(prev => {
      const categoryData = prev[categoryId] || { items: [] };
      const items = categoryData.items.filter(item => 
        (item.tempId || item.id) !== itemId
      );
      
      return {
        ...prev,
        [categoryId]: {
          ...categoryData,
          items
        }
      };
    });
    
    toast.success('Item deleted from BOQ');
  };
  
  /**
   * Save BOQ to backend
   */
  const handleSave = async () => {
    setIsSaving(true);
    
    try {
      // Save each category that has items
      const savePromises = categories
        .filter(category => boqData[category.id]?.items?.length > 0)
        .map(category => {
          const categoryData = boqData[category.id];
          
          // Prepare request
          const request = {
            segmentId: segment.id,
            budgetCategoryId: category.id,
            remarks: categoryData.remarks || '',
            items: categoryData.items.map(item => ({
              itemId: item.itemId,
              volume: parseFloat(item.volume) || 0,
              isPriceOverridden: item.isPriceOverridden || false,
              overriddenUnitPrice: item.isPriceOverridden ? parseFloat(item.overriddenUnitPrice) : null,
              remarks: item.remarks || '',
              sequenceOrder: item.sequenceOrder || 0
            }))
          };
          
          return foSegmentBOQService.saveBOQ(request);
        });
      
      await Promise.all(savePromises);
      
      toast.success('BOQ saved successfully!');
      
      // Callback to parent
      if (onSave) {
        onSave(segment.id);
      }
      
    } catch (error) {
      console.error('❌ Error saving BOQ:', error);
      toast.error('Failed to save BOQ');
    } finally {
      setIsSaving(false);
    }
  };

  // ============= RENDER HELPERS =============
  
  /**
   * Render single BOQ item row
   */
  const renderItemRow = (categoryId, item, index) => {
    const priceAdjustmentPct = item.baseUnitPrice > 0 ? 
      ((item.adjustedUnitPrice - item.baseUnitPrice) / item.baseUnitPrice * 100) : 0;
    
    return (
      <tr key={item.tempId || item.id} className="border-b border-gray-200 hover:bg-gray-50">
        {/* No */}
        <td className="px-4 py-3 text-center text-sm text-gray-700 font-medium">
          {index + 1}
        </td>
        
        {/* Item Info */}
        <td className="px-4 py-3">
          <div className="flex items-start gap-2">
            <Package className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs text-gray-600">
                  {item.itemCode || item.itemNumber}
                </span>
                <span className="text-sm font-semibold text-gray-900 truncate">
                  {item.itemDescription}
                </span>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Type: {item.itemType}
              </div>
            </div>
          </div>
        </td>
        
        {/* Unit */}
        <td className="px-4 py-3 text-sm text-gray-700 text-center">
          {item.unit}
        </td>
        
        {/* Volume */}
        <td className="px-4 py-3">
          <input
            type="number"
            value={item.volume}
            onChange={(e) => handleItemFieldChange(categoryId, item.tempId || item.id, 'volume', e.target.value)}
            min="0"
            step="0.01"
            className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </td>
        
        {/* Price Info */}
        <td className="px-4 py-3">
          <div className="space-y-1">
            {/* Base Price */}
            <div className="text-xs text-gray-500">
              Base: {formatCurrency(item.baseUnitPrice)}
            </div>
            
            {/* Coefficient & Adjustment */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-blue-600 font-semibold">
                ×{item.priceCoefficient.toFixed(4)}
              </span>
              <span className={`text-xs font-semibold ${
                priceAdjustmentPct >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {formatPercentage(priceAdjustmentPct)}
              </span>
            </div>
            
            {/* Adjusted Price */}
            <div className="text-sm font-semibold text-gray-900">
              {formatCurrency(item.adjustedUnitPrice)}
            </div>
          </div>
        </td>
        
        {/* Override */}
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={item.isPriceOverridden}
              onChange={(e) => handleItemFieldChange(categoryId, item.tempId || item.id, 'isPriceOverridden', e.target.checked)}
              className="w-4 h-4 text-blue-600"
            />
            {item.isPriceOverridden && (
              <input
                type="number"
                value={item.overriddenUnitPrice}
                onChange={(e) => handleItemFieldChange(categoryId, item.tempId || item.id, 'overriddenUnitPrice', e.target.value)}
                min="0"
                step="0.01"
                className="w-32 px-2 py-1 border border-gray-300 rounded text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            )}
          </div>
        </td>
        
        {/* Total Price */}
        <td className="px-4 py-3 text-right">
          <span className="text-sm font-bold text-blue-600">
            {formatCurrency(item.totalPrice)}
          </span>
        </td>
        
        {/* Actions */}
        <td className="px-4 py-3 text-center">
          <button
            onClick={() => handleDeleteItem(categoryId, item.tempId || item.id)}
            className="text-red-600 hover:text-red-800 p-1.5 hover:bg-red-50 rounded transition-colors"
            title="Delete Item"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </td>
      </tr>
    );
  };

  // ============= MAIN RENDER =============
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              disabled={isSaving}
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">FO Segment Bill of Quantity</h1>
              <p className="text-gray-600 mt-1">{segment.siteNameAB}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Segment Info */}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-4 border border-green-200">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="text-xs font-semibold text-gray-600">Span ID</label>
            <p className="text-sm font-bold text-gray-900">{segment.spanId || '-'}</p>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600">Main Area</label>
            <p className="text-sm font-bold text-gray-900">{segment.mainArea || segment.main_area || '-'}</p>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600">Price Coefficient</label>
            <p className="text-sm font-bold text-blue-600">×{priceCoefficient.toFixed(4)}</p>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600">Distance</label>
            <p className="text-sm font-bold text-gray-900">
              {segment.commercialDistance 
                ? (segment.commercialDistance / 1000).toFixed(2) 
                : segment.length || '0'} km
            </p>
          </div>
        </div>
      </div>

      {/* BOQ Categories */}
      {isLoading || isLoadingCategories ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-600">Loading BOQ data...</p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {categories.map(category => {
            const isExpanded = expandedCategories.includes(category.id);
            const items = boqData[category.id]?.items || [];
            const subtotal = calculateSubtotal(category.id);
            
            return (
              <div key={category.id} className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
                {/* Category Header */}
                <div
                  className="bg-gradient-to-r from-green-600 to-green-500 text-white p-4 cursor-pointer hover:from-green-700 hover:to-green-600 transition-colors"
                  onClick={() => toggleCategory(category.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {isExpanded ? (
                        <ChevronDown className="w-5 h-5" />
                      ) : (
                        <ChevronRight className="w-5 h-5" />
                      )}
                      <div>
                        <h3 className="text-lg font-bold">
                          {category.categoryCode} - {category.categoryName}
                        </h3>
                        <p className="text-sm text-green-100 mt-1">
                          {items.length} items • Subtotal: {formatCurrency(subtotal)}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddItem(category.id);
                      }}
                      className="flex items-center gap-2 bg-white text-green-600 px-4 py-2 rounded-lg hover:bg-green-50 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      Add Item
                    </button>
                  </div>
                </div>

                {/* Category Content */}
                {isExpanded && (
                  <div className="p-4">
                    {items.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p>No items in this category</p>
                        <p className="text-sm mt-1">Click "Add Item" to start</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50 border-b-2 border-gray-200">
                            <tr>
                              <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase text-center">No</th>
                              <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase text-left">Item</th>
                              <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase text-center">Unit</th>
                              <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase text-center">Volume</th>
                              <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase text-left">Price Info</th>
                              <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase text-center">Override</th>
                              <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase text-right">Total</th>
                              <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase text-center">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {items.map((item, index) => renderItemRow(category.id, item, index))}
                          </tbody>
                          <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                            <tr>
                              <td colSpan="6" className="px-4 py-3 text-right font-semibold text-gray-700">
                                Subtotal:
                              </td>
                              <td className="px-4 py-3 text-right font-bold text-green-600 text-lg">
                                {formatCurrency(subtotal)}
                              </td>
                              <td></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Grand Total & Actions */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center">
              <Info className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Grand Total</p>
              <p className="text-2xl font-bold text-blue-600">
                {formatCurrency(calculateGrandTotal())}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              disabled={isSaving}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors shadow-md hover:shadow-lg flex items-center gap-2 disabled:opacity-50"
            >
              <Save className="w-5 h-5" />
              {isSaving ? 'Saving...' : 'Save BOQ'}
            </button>
          </div>
        </div>
      </div>

      {/* Item Picker Modal */}
      <ItemPickerModal
        isOpen={showItemPicker}
        onClose={() => setShowItemPicker(false)}
        onSelect={handleItemSelect}
        filterType="ALL"
      />
    </div>
  );
};

export default FOSegmentBOQForm;